import {
  BlotterDensity,
  BlotterTable,
  Box,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  HedgeControlStatusEnum,
  MixpanelEventSource,
  MixpanelSourceProvider,
  WARNING_ROW_CLASSNAME,
  useBlotterTable,
  type BlotterTableClientLocalFilter,
} from '@talos/kyoko';

import { usePositionAutoHedgingRulesDrawer } from '@src/components/AutoHedgingRulesDrawer/AutoHedgingRulesDrawer';
import { useHedgePositionStatusColumns } from '@src/containers/Blotters/HedgePositionStatus/columns';
import { useHedgePositionStatusMenu } from '@src/containers/Blotters/HedgePositionStatus/useHedgePositionStatusMenu';
import { useHedgePositionStatus, type IHedgePositionStatusRow } from '@src/providers/HedgePositionStatusProvider';
import type { GridOptions } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';

/**
 * Small table for displaying the status of the autohedging positions.
 */
export function SmallHedgePositionStatusBlotterTable({
  status,
  search,
}: {
  status: HedgeControlStatusEnum | undefined;
  search: string;
}) {
  const clientRowFilter: BlotterTableClientLocalFilter<IHedgePositionStatusRow> = useCallback(
    row => {
      if (!status) {
        return true;
      }
      if (row.data?.HedgeControlStatus === status) {
        return true;
      }
      return false;
    },
    [status]
  );

  const { hedgePositionStatusDeltasObs } = useHedgePositionStatus();
  const { openPositionAutoHedgingRulesDrawer } = usePositionAutoHedgingRulesDrawer();

  const columns = useHedgePositionStatusColumns({ size: 'mini' });
  const { getCustomContextMenuItems } = useHedgePositionStatusMenu();

  const gridOptionsMemoized = useMemo(() => {
    return {
      onRowDoubleClicked: ({ data }) => {
        data != null && openPositionAutoHedgingRulesDrawer({ hedgeRuleID: data.HedgeRuleID });
      },
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      rowClassRules: {
        [WARNING_ROW_CLASSNAME]: params => params?.data?.HedgeControlStatus === HedgeControlStatusEnum.Error,
      },
      defaultColDef: { suppressHeaderMenuButton: true },
    } satisfies GridOptions<IHedgePositionStatusRow>;
  }, [openPositionAutoHedgingRulesDrawer]);

  const blotterTable = useBlotterTable<IHedgePositionStatusRow>({
    dataObservable: hedgePositionStatusDeltasObs,
    errorObservable: undefined, // doable to add but tricky
    columns: columns,
    rowID: 'HedgeRuleID',
    clientLocalFilter: clientRowFilter,
    quickSearchParams: {
      filterText: search,
    },
    contextMenuOptions: { getCustomContextMenuItems },
    gridOptions: gridOptionsMemoized,
  });

  const theme = useTheme();
  return (
    <>
      <Box w="100%" h="200px">
        <MixpanelSourceProvider value={MixpanelEventSource.PositionAutoHedgingMiniBlotter}>
          <BlotterTable
            {...blotterTable}
            density={BlotterDensity.VeryComfortable}
            background={theme.colors.gray['040']}
          />
        </MixpanelSourceProvider>
      </Box>
    </>
  );
}
